import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    debug: true, // Aktifkan debug untuk membantu troubleshooting
    storageKey: 'cara_auth_token', // Kunci penyimpanan khusus untuk mencegah konflik
    storage: localStorage, // Pastikan menggunakan localStorage
    cookieOptions: {
      secure: window.location.protocol === 'https:', // Gunakan secure cookies di HTTPS
      sameSite: 'Lax' // Pengaturan SameSite yang lebih aman
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'cara-v17', // Update versi aplikasi
      'apikey': supabaseAnonKey,
      'Authorization': `Bearer ${supabaseAnonKey}`
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

// Helper functions for common operations
export const supabaseHelpers = {
  // Auth helpers
  async signUp(email, password, metadata = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    })
    return { data, error }
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  },

  // Database helpers
  async insertData(table, data) {
    const { data: result, error } = await supabase
      .from(table)
      .insert(data)
      .select()
    return { data: result, error }
  },

  async updateData(table, id, data) {
    const { data: result, error } = await supabase
      .from(table)
      .update(data)
      .eq('id', id)
      .select()
    return { data: result, error }
  },

  async deleteData(table, id) {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id)
    return { error }
  },

  async fetchData(table, filters = {}) {
    let query = supabase.from(table).select('*')
    
    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value)
    })
    
    const { data, error } = await query
    return { data, error }
  }
}

// Initialize session and headers on startup
;(async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) {
      console.error('Initial session error:', error)
    } else if (session) {
      console.log('Initial session found, updating headers')
      const headers = {
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${session.access_token}`,
        'X-Client-Info': 'cara-v17'
      }
      Object.assign(supabase.rest.headers, headers)
      Object.assign(supabase.storage.headers, headers)
      
      // Verifikasi token dan refresh jika perlu
      const tokenExpiry = new Date(session.expires_at * 1000)
      const now = new Date()
      const timeUntilExpiry = tokenExpiry - now
      
      if (timeUntilExpiry < 600000) { // Kurang dari 10 menit
        console.log('Token hampir kedaluwarsa, melakukan refresh...')
        await sessionHelpers.refreshSession()
      }
    }
  } catch (error) {
    console.error('Failed to initialize session:', error)
  }
})()

// Request interceptor to ensure proper headers
supabase.auth.onAuthStateChange((event, session) => {
  console.log('Auth state change:', event, !!session)
  
  // Update global headers for all requests
  const headers = {
    'apikey': supabaseAnonKey,
    'X-Client-Info': 'cara-v17'
  }
  
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`
    console.log('Updated headers with user access token')
    
    // Simpan token di localStorage sebagai cadangan
    try {
      localStorage.setItem('cara_access_token', session.access_token)
      localStorage.setItem('cara_refresh_token', session.refresh_token)
    } catch (e) {
      console.error('Failed to store tokens in localStorage:', e)
    }
    
    // Log event autentikasi untuk debugging
    if (event === 'SIGNED_IN') {
      console.log('User signed in successfully')
    } else if (event === 'TOKEN_REFRESHED') {
      console.log('Token refreshed successfully')
    }
  } else {
    headers['Authorization'] = `Bearer ${supabaseAnonKey}`
    console.log('Updated headers with anon key')
    
    // Hapus token dari localStorage jika sign out
    if (event === 'SIGNED_OUT') {
      try {
        localStorage.removeItem('cara_access_token')
        localStorage.removeItem('cara_refresh_token')
        console.log('Tokens removed from localStorage')
      } catch (e) {
        console.error('Failed to remove tokens from localStorage:', e)
      }
    }
  }
  
  // Update the client's global headers
  Object.assign(supabase.rest.headers, headers)
  Object.assign(supabase.storage.headers, headers)
})

// Session management helpers
export const sessionHelpers = {
  async refreshSession() {
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error) {
        console.error('Session refresh error:', error)
        return { success: false, error }
      }
      return { success: true, session: data.session }
    } catch (error) {
      console.error('Session refresh failed:', error)
      return { success: false, error }
    }
  },

  async validateSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.error('Session validation error:', error)
        return { valid: false, error }
      }
      
      if (!session) {
        return { valid: false, error: 'No active session' }
      }
      
      // Check if session is expired
      if (session.expires_at && new Date(session.expires_at * 1000) < new Date()) {
        console.log('Session expired, attempting refresh...')
        const refreshResult = await this.refreshSession()
        return { valid: refreshResult.success, session: refreshResult.session, error: refreshResult.error }
      }
      
      return { valid: true, session }
    } catch (error) {
      console.error('Session validation failed:', error)
      return { valid: false, error }
    }
  }
}

export default supabase